/**
 * Main Entry Point
 * For Media Devoted | Created By Ciro Autiero
 *
 * Note: React.StrictMode causes double-mounting in development to detect side effects.
 * Queries with refetchOnMount: false (e.g. useGatewaySenderTemplates) avoid duplicate
 * fetches when remounting. This is expected dev behavior; production single-mounts.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import App from './App';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ThemeProvider } from '@/providers/ThemeProvider';
import { AuthProvider } from '@/providers/AuthProvider';
import { MemeLoaderPreferenceProvider } from '@/providers/MemeLoaderPreferenceProvider';
import { ToastProvider } from '@/providers/ToastProvider';
import { printTrafficPulseDevtoolsBanner } from '@/lib/print-devtools-console-art';
import './styles/globals.css';

printTrafficPulseDevtoolsBanner();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter
        future={{
          v7_relativeSplatPath: true,
        }}
      >
        <ThemeProvider>
          <AuthProvider>
            <MemeLoaderPreferenceProvider>
              <ToastProvider>
                <ErrorBoundary>
                  <App />
                </ErrorBoundary>
              </ToastProvider>
            </MemeLoaderPreferenceProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </React.StrictMode>,
);
